import React, { useState } from 'react';
import { Modal, Card, Button, Typography, Space, Input, QRCode, message, Tooltip } from 'antd';
import { QrcodeOutlined, CopyOutlined, DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const QRCodeModal = ({ visible, onClose, item, facilityId }) => {
  const [qrSize, setQRSize] = useState(200);
  
  if (!item || !facilityId) return null;

  // Generate QR code URL
  const baseUrl = window.location.origin;
  const qrUrl = `${baseUrl}/${facilityId}/item/${item.baseId || item.name}`;
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${type} copied to clipboard!`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const downloadQR = (url, filename) => {
    const canvas = document.querySelector('.qr-code-canvas canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <QrcodeOutlined />
          QR Code Generator - {item.name}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      className="qr-modal"
    >
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <Card title="Item QR Code" size="small">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-md)' }}>
            <div className="qr-code-canvas">
              <QRCode
                value={qrUrl}
                size={qrSize}
                style={{ marginBottom: 'var(--spacing-md)' }}
              />
            </div>
            
            <div style={{ marginBottom: 'var(--spacing-sm)' }}>
              <Text strong>QR Size:</Text>
              <Input
                type="number"
                value={qrSize}
                onChange={(e) => setQRSize(Number(e.target.value))}
                min={100}
                max={400}
                step={25}
                style={{ width: '80px', marginLeft: '8px' }}
              />
            </div>
          </div>
          
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text strong>Purpose:</Text>
              <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                Customers can scan to view item details and continue shopping
              </Paragraph>
            </div>
            
            <div>
              <Text strong>URL:</Text>
              <Input.Group compact>
                <Input
                  value={qrUrl}
                  readOnly
                  style={{ width: 'calc(100% - 32px)' }}
                />
                <Tooltip title="Copy URL">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(qrUrl, 'URL')}
                  />
                </Tooltip>
              </Input.Group>
            </div>
            
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => downloadQR(qrUrl, `${item.name}-qr.png`)}
                type="primary"
              >
                Download QR Code
              </Button>
              <Button
                icon={<ShareAltOutlined />}
                onClick={() => copyToClipboard(qrUrl, 'Link')}
              >
                Share Link
              </Button>
            </Space>
          </Space>
        </Card>
      </div>

      {/* Usage Instructions */}
      <Card title="Usage Instructions" size="small" style={{ marginTop: 'var(--spacing-lg)' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Title level={5}>How it Works:</Title>
            <ul style={{ fontSize: '12px', paddingLeft: '16px', margin: 0 }}>
              <li>Print and place QR code on shelf or product</li>
              <li>Customers scan with phone camera</li>
              <li>They see item details and can continue shopping</li>
              <li>Shopping cart is presented at checkout</li>
            </ul>
          </div>
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              💡 Recommended print size: 200px minimum for reliable scanning
            </Text>
          </div>
        </Space>
      </Card>
    </Modal>
  );
};

export default QRCodeModal;
